import React, { useEffect, useState } from "react";
import qs from "qs";
import { publicApi } from "../../services/api";
import { saveBlobAsFile } from "../../services/FileDownloadUtil";
import {
  addSessionClosedId,
  filterVisibleNotices,
  setHideCookie,
} from "../../services/NoticePopupUtil";
import NoticePopup, {
  POPUP_MAX_WIDTH,
  POPUP_WIDTH_RATIO,
} from "./NoticePopup";

/** 계단식 오프셋 간격 - 브라우저 창 크기에 비례하되 20~40px 범위로 제한한다. */
const OFFSET_MIN = 20;
const OFFSET_MAX = 40;

/**
 * 계단식 배치 간격을 계산한다.
 * 팝업 개수만큼 어긋나게 배치했을 때 화면을 벗어나면 오프셋을 적용하지 않는다(중앙 정렬).
 */
const calculateOffset = (count) => {
  if (count <= 1) {
    return 0;
  }

  const offset = Math.min(
    OFFSET_MAX,
    Math.max(OFFSET_MIN, Math.round(window.innerWidth * 0.02))
  );
  const popupWidth = Math.min(POPUP_MAX_WIDTH, window.innerWidth * POPUP_WIDTH_RATIO);
  const requiredWidth = (window.innerWidth + popupWidth) / 2 + offset * (count - 1);

  return requiredWidth > window.innerWidth ? 0 : offset;
};

/**
 * 팝업 공지 표출 컨테이너
 *
 * 노출 화면(target)별로 현재 노출 대상 공지를 조회해 표출한다.
 * 조회·다운로드는 인증이 필요 없는 공개 API를 사용하므로 로그인 화면에서도 동작한다.
 */
const NoticePopupList = ({ target }) => {
  const [notices, setNotices] = useState([]);
  // 다운로드에 실패한 공지 id. 해당 팝업에만 오류 문구를 표출한다.
  const [downloadErrorId, setDownloadErrorId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    publicApi
      .post("/notice/popup/list", { popupTarget: target })
      .then((response) => {
        if (isMounted) {
          setNotices(filterVisibleNotices(target, response?.data));
        }
      })
      .catch((error) => {
        // 팝업 조회 실패는 화면 진입을 막지 않는다.
        console.error("Error loading popup notices:", error);
      });

    return () => {
      isMounted = false;
    };
  }, [target]);

  const handleClose = (notice, hideAgain) => {
    if (hideAgain) {
      setHideCookie(target, notice.id, notice.popupEndDtm);
    }
    addSessionClosedId(target, notice.id);
    setNotices((prev) => prev.filter((item) => item.id !== notice.id));
  };

  const handleDownload = async (id, fileName) => {
    if (!fileName) {
      return;
    }

    setDownloadErrorId(null);

    try {
      const response = await publicApi.post(
        "/notice/popup/download",
        qs.stringify({ id, fileName }),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          responseType: "blob",
        }
      );
      saveBlobAsFile(response.data, fileName);
    } catch (error) {
      console.error("Error downloading popup file:", error);
      // 실패를 조용히 넘기면 사용자가 이유를 알 수 없다.
      setDownloadErrorId(id);
    }
  };

  if (notices.length === 0) {
    return null;
  }

  const offset = calculateOffset(notices.length);

  return (
    <>
      {notices.map((notice, index) => (
        <NoticePopup
          key={notice.id}
          notice={notice}
          offset={offset * index}
          onClose={handleClose}
          onDownload={handleDownload}
          downloadError={downloadErrorId === notice.id}
        />
      ))}
    </>
  );
};

export default NoticePopupList;
